import type { LocationQuery, LocationQueryRaw } from "vue-router";

import { isInAppReturnPath } from "@/router/route-manager";

/**
 * Why the /access-required (403) page was shown (UX roadmap #50).
 *
 * - 'locked': the member's grants were checked and do not include the page.
 * - 'unverified': the grants could not be loaded (a network blip, an expired session), so nothing is known about
 *   access. The page used to say "your session may have expired" to both, and offered neither the product nor a retry.
 */
export type ForbiddenReason = 'locked' | 'unverified';

/** What the 403 page is told about the page it replaced. Every field is optional: a bare /access-required still works. */
export interface ForbiddenContext {
    /** The in-app path that was refused, query and hash included. */
    from?: string;
    /** The route's `meta.permission` (a feature key). */
    need?: string;
    /** The route's `meta.shortCode` (a product code, e.g. 'SSS'). */
    code?: string;
    /** A product slug, for callers that know the product itself (a model portfolio). */
    product?: string;
    reason?: ForbiddenReason;
}

export const ForbiddenQueryKeys = {
    from: 'from',
    need: 'need',
    code: 'code',
    product: 'product',
    reason: 'reason',
} as const;

const REASONS: readonly ForbiddenReason[] = [ 'locked', 'unverified' ];

function firstValue( value: LocationQuery[ string ] | undefined ): string | undefined {
    const first = Array.isArray( value ) ? value[ 0 ] : value;
    return first ? `${ first }` : undefined;
}

/** The query for a redirect to the 403 page. Empty fields are left out, so the URL carries only what is known. */
export function forbiddenQuery( context: ForbiddenContext ): LocationQueryRaw {
    const query: LocationQueryRaw = {};
    for ( const key of Object.values( ForbiddenQueryKeys ) ) {
        const value = context[ key ];
        if ( value ) {
            query[ key ] = value;
        }
    }
    return query;
}

/**
 * Reads the 403 page's query back. The URL is user-editable, so `from` must be an in-app path (never '//host') and an
 * unknown reason is dropped.
 */
export function readForbiddenQuery( query: LocationQuery ): ForbiddenContext {
    const from = firstValue( query[ ForbiddenQueryKeys.from ] );
    const reason = firstValue( query[ ForbiddenQueryKeys.reason ] );
    return {
        from: isInAppReturnPath( from ) ? from : undefined,
        need: firstValue( query[ ForbiddenQueryKeys.need ] ),
        code: firstValue( query[ ForbiddenQueryKeys.code ] ),
        product: firstValue( query[ ForbiddenQueryKeys.product ] ),
        reason: REASONS.includes( reason as ForbiddenReason ) ? reason as ForbiddenReason : undefined,
    };
}
